import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Link2, Copy, ArrowRight, LogIn, Video, Phone } from "lucide-react";
import { toast } from "sonner";

/* Screen 10 — Practice With Friend (approved SalesPit design). No LiveKit yet: uses the placeholder Video Call. */

const genCode = () =>
  Array.from(
    { length: 6 },
    () => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 32)]
  ).join("");

export default function Practice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitedRoom = searchParams.get("room");
  // Call mode: the creator picks one and it rides inside the invite link
  // (?mode=phone) so the friend joins the same experience. Code-only joins
  // have no mode carrier and stay camera-on.
  const invitedMode = searchParams.get("mode") === "phone" ? "phone" : "video";

  const [room, setRoom] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [callMode, setCallMode] = useState("video");

  const inviteLink = useMemo(
    () =>
      room
        ? `${window.location.origin}/practice?room=${room}${
            callMode === "phone" ? "&mode=phone" : ""
          }`
        : "",
    [room, callMode]
  );

  const createInvite = () => setRoom(genCode());

  const copyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard
      ?.writeText(inviteLink)
      .then(() => toast.success("Invite link copied!"))
      .catch(() => toast.error("Couldn't copy the link."));
  };

  const startSession = () => {
    const code = invitedRoom || room || genCode();
    navigate("/video-call", {
      state: {
        mode: "friend",
        room: `friend-${code.toLowerCase()}`,
        cameraOn: (invitedRoom ? invitedMode : callMode) !== "phone",
      },
    });
  };

  const joinRoom = () => {
    const code = joinCode.trim();
    if (code.length < 4) {
      toast.error("Enter a valid room code.");
      return;
    }
    navigate("/video-call", {
      state: { mode: "friend", room: `friend-${code.toLowerCase()}` },
    });
  };

  return (
    <main
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0A0A] px-6 py-16 font-body text-white"
      data-testid="practice-page"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/3 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.12),transparent_62%)] blur-3xl"
      />

      <button
        data-testid="back-btn"
        onClick={() => navigate("/homepage")}
        className="absolute left-6 top-6 text-sm font-medium text-zinc-500 transition-colors duration-300 hover:text-white"
      >
        ← Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-[#E52525]/30 bg-[#E52525]/10 text-[#E52525]">
            <Users size={26} />
          </span>
          <h1 className="font-heading mt-6 text-3xl font-black tracking-tight text-white sm:text-4xl">
            Practice With a Friend
          </h1>
          <p className="mt-3 text-base font-light text-zinc-400">
            Skip matchmaking and run a private mock call with someone you know.
          </p>
        </div>

        {invitedRoom ? (
          /* Guest arrived via invite link */
          <div
            className="mt-10 rounded-3xl border border-[#E52525]/25 bg-[#141414] p-7 text-center"
            data-testid="invite-accept-card"
          >
            <p className="text-sm text-zinc-400">You've been invited to room</p>
            <p
              data-testid="room-code"
              className="font-heading mt-2 text-3xl font-black tracking-[0.3em] text-white"
            >
              {invitedRoom}
            </p>
            <p
              data-testid="invite-mode-hint"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#E52525]/30 bg-[#E52525]/10 px-3 py-1 text-xs font-semibold text-[#FF5A5A]"
            >
              {invitedMode === "phone" ? (
                <>
                  <Phone size={12} /> Phone mock — your camera stays off
                </>
              ) : (
                <>
                  <Video size={12} /> Video mock — camera on
                </>
              )}
            </p>
            <button
              data-testid="join-session-btn"
              onClick={startSession}
              className="mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E52525] px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.3)] transition-[background-color,transform] duration-300 hover:bg-[#FF3333] active:scale-[0.98]"
            >
              Join Session
              <ArrowRight size={19} />
            </button>
          </div>
        ) : (
          <>
            {/* Create / share invite */}
            <div className="mt-10 rounded-3xl border border-white/10 bg-[#141414] p-7">
              {/* Call mode — encoded into the invite link so the friend
                  joins the same experience. */}
              <div className="mb-6" data-testid="practice-call-mode">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                  Call mode
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: "video", icon: Video, label: "Camera On", testid: "practice-mode-camera-on" },
                    { id: "phone", icon: Phone, label: "Camera Off", testid: "practice-mode-camera-off" },
                  ].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      data-testid={m.testid}
                      aria-pressed={callMode === m.id}
                      onClick={() => setCallMode(m.id)}
                      className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                        callMode === m.id
                          ? "border-[#E52525]/60 bg-[#E52525]/10 text-white"
                          : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"
                      }`}
                    >
                      <m.icon
                        size={15}
                        className={callMode === m.id ? "text-[#E52525]" : ""}
                      />
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {!room ? (
                <div className="text-center">
                  <h2 className="font-heading text-lg font-bold text-white">
                    Create a private room
                  </h2>
                  <p className="mt-2 text-sm text-zinc-400">
                    Generate an invite link to share with your practice partner.
                  </p>
                  <button
                    data-testid="create-invite-btn"
                    onClick={createInvite}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E52525] px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.3)] transition-[background-color,transform] duration-300 hover:bg-[#FF3333] active:scale-[0.98]"
                  >
                    <Link2 size={19} />
                    Create Invite
                  </button>
                </div>
              ) : (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-white">
                      Your invite is ready
                    </span>
                    <span
                      data-testid="room-code"
                      className="rounded-lg bg-[#E52525]/10 px-2.5 py-1 text-xs font-bold tracking-[0.2em] text-[#E52525]"
                    >
                      {room}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="invite-link"
                      readOnly
                      value={inviteLink}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 truncate rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-zinc-300 focus:outline-none"
                    />
                    <button
                      data-testid="copy-link-btn"
                      onClick={copyLink}
                      aria-label="Copy invite link"
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white transition-colors duration-200 hover:bg-white/20"
                    >
                      <Copy size={18} />
                    </button>
                  </div>
                  <button
                    data-testid="start-session-btn"
                    onClick={startSession}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E52525] px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.3)] transition-[background-color,transform] duration-300 hover:bg-[#FF3333] active:scale-[0.98]"
                  >
                    Start Session
                    <ArrowRight size={19} />
                  </button>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-600">
                or
              </span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            {/* Join with code */}
            <div className="rounded-3xl border border-white/10 bg-[#141414] p-7">
              <h2 className="font-heading text-lg font-bold text-white">
                Join with a code
              </h2>
              <p className="mt-2 text-sm text-zinc-400">
                Got a room code from a friend? Enter it below.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="join-code-input"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="Enter room code"
                  className="flex-1 rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm uppercase tracking-[0.2em] text-white placeholder:tracking-normal placeholder:text-zinc-500 focus:border-[#E52525]/50 focus:outline-none"
                />
                <button
                  data-testid="join-room-btn"
                  onClick={joinRoom}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-transparent px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/5"
                >
                  <LogIn size={17} />
                  Join Room
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </main>
  );
}
